import React from 'react';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro';
import { TFunction } from 'i18next';
import { FixedSkuColumns } from '../SkuView/SkuTable/columns';
import GridCellWithTooltip from '../components/GridCellWithTooltip';
import { DataRowObject, sanitizeDataRowObject } from './utils';
import { FixedArticleColumns, getColumnName } from '../ArticlesView/ArticlesTable/columns';
import { Box } from '@mui/material';
import { articleStatusesFromString } from './parse';
import { StatusCell } from '../components/StatusCell';

export type ImportTableVariant = 'toAdd' | 'toUpdate' | 'invalid';

export const UPDATED_FIELDS_ROW_KEY = '__updated_fields__';
export const OLD_ARTICLE_CODE_KEY = 'old_code';
export const INVALID_FIELDS_ROW_KEY = '__invalid_fields__';

const renderUpdatedTableCell = (field: string, params: GridRenderCellParams) => {
	const updatedFields = params.getValue(params.id, UPDATED_FIELDS_ROW_KEY) as string[];
	if (updatedFields.includes(field)) {
		let value: string | JSX.Element = params.value;
		if (field === 'status') {
			const statusValue = articleStatusesFromString(params.value);
			if (!!statusValue) {
				value = <StatusCell value={statusValue} chipColoring="success" />;
			}
		}
		return (
			<Box component="span" sx={{ color: (theme) => theme.palette.success.main }}>
				{value}
			</Box>
		);
	}
	if (field === OLD_ARTICLE_CODE_KEY) {
		return (
			<Box
				component="span"
				sx={{ color: (theme) => theme.palette.secondary.main, fontStyle: 'italic' }}
			>
				{params.value}
			</Box>
		);
	}
	return params.value;
};

const renderInvalidTableCell = (field: string, params: GridRenderCellParams, t: TFunction) => {
	const invalidFields = params.getValue(params.id, INVALID_FIELDS_ROW_KEY) as {
		[x: string]: string | undefined;
	};
	if (!!invalidFields[field]) {
		let value: string | JSX.Element = params.value || t('validation.empty', 'Empty');
		if (field === 'status') {
			const statusValue = articleStatusesFromString(params.value);
			if (!!statusValue) {
				value = <StatusCell value={statusValue} chipColoring="error" />;
			}
		}
		return (
			<GridCellWithTooltip
				value={
					<Box component="span" sx={{ color: (theme) => theme.palette.error.main }}>
						{value}
					</Box>
				}
				width={params.colDef.computedWidth - 10}
				tooltipWidth={200}
				tooltipValue={invalidFields[field] ?? undefined}
				tooltipOnHover="always"
				cellOverflow="visible"
			/>
		);
	}
	return params.value;
};

export const getSkuTableColumns = (
	t: TFunction,
	tableVariant: ImportTableVariant,
	headerData: DataRowObject,
): GridColDef[] => {
	const { sku_code, sku_name, type } = FixedSkuColumns;
	const skuColumnFields = [sku_code, sku_name, type];
	const baseColumns = skuColumnFields.map((field) => ({
		field,
		headerName: headerData[field] ?? getColumnName(field, t),
		width: 110,
	}));
	if (tableVariant === 'toUpdate') {
		return baseColumns.map((col) => ({
			...col,
			renderCell: (params) => renderUpdatedTableCell(col.field, params),
		}));
	}
	if (tableVariant === 'invalid') {
		return baseColumns.map((col) => ({
			...col,
			renderCell: (params) => renderInvalidTableCell(col.field, params, t),
		}));
	}
	return baseColumns;
};

export const getArticleTableColumns = (
	t: TFunction,
	tableVariant: ImportTableVariant,
	headerData: DataRowObject,
	requiredFields?: string[],
): GridColDef[] => {
	const fieldsFromHeader = Object.keys(sanitizeDataRowObject(headerData, null));
	const validColumns = [...(requiredFields ?? []), ...fieldsFromHeader].filter(
		(col) => col !== 'id',
	);
	const columnOrder = Object.keys(FixedArticleColumns);
	const sortedColumns = validColumns.sort((col1, col2) => {
		if (col1 === OLD_ARTICLE_CODE_KEY) return -1;
		if (col2 === OLD_ARTICLE_CODE_KEY) return 1;
		const indexCol1 = columnOrder.indexOf(col1);
		const indexCol2 = columnOrder.indexOf(col2);
		if (indexCol1 === indexCol2) return 0;
		if (indexCol1 === -1 || indexCol1 > indexCol2) return 1;
		return -1;
	});
	const baseColumns: GridColDef[] = sortedColumns.map((field) => {
		return {
			field,
			headerName: headerData[field] ?? getColumnName(field, t),
			width: 110,
			renderCell: (params) => {
				if (field === 'status') {
					const statusValue = articleStatusesFromString(params.value);
					if (!!statusValue) {
						return <StatusCell value={statusValue} chipColoring="neutral" />;
					}
				}
				return undefined;
			},
			cellClassName: 'overflow-cell',
		};
	});
	if (tableVariant === 'toUpdate') {
		return baseColumns.map((col) => ({
			...col,
			headerName: col.field === OLD_ARTICLE_CODE_KEY ? 'ID' : col.headerName,
			renderCell: (params) => renderUpdatedTableCell(col.field, params),
		}));
	}
	if (tableVariant === 'invalid') {
		return baseColumns.map((col) => ({
			...col,
			renderCell: (params) => renderInvalidTableCell(col.field, params, t),
		}));
	}
	return baseColumns;
};
